import { ArrowForwardIos, FiberManualRecord } from '@mui/icons-material';
import { IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { useMemo, useState } from 'react';
import { Link } from '#components/Link/index.tsx';
import { PlayerSelectorTabs } from '#components/PlayerSelectorTabs/index.tsx';
import { Section, SectionColor } from '#components/Section/index.tsx';
import { useIsAncestor } from '#contexts/Ancestors/index.ts';
import { Ancestor } from '#enums/ancestors.ts';
import { ImageSize, PosterSize } from '#enums/imageSizes.ts';
import { ScreenFormatType, SectionType } from '#enums/mediaTypes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { formatAirDate } from '#helpers/formatAirDate.ts';
import { formatGenres } from '#helpers/formatGenres.ts';
import { formatNumberOfEpisodes } from '#helpers/formatNumberOfEpisodes.ts';
import { disable as disableFullscreenLock } from '#helpers/fullscreenLock.ts';
import { getTvCertFromContentRatings } from '#helpers/getTvCertFromContentRatings.ts';
import { get } from '#helpers/otherHelpers.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { SectionHeading } from '#styled.ts';
import { type ActiveVideo } from '#types.ts';
import { CastAndCrewCarousel } from '../../CastAndCrew/Carousel/index.tsx';
import { Query as CastAndCrewHighlights } from '../../CastAndCrew/Highlights/index.ts';
import { Component as CertificationIcon } from '../../CertificationIcon/index.ts';
import { ConditionalTextLink } from '../../ConditionalTextLink/index.tsx';
import { EpisodesCarousel } from '../../EpisodesCarousel/index.tsx';
import { PlayerSelectorModal } from '../../Modal/PlayerSelectorModal.tsx';
import { Overview } from '../../Overview/index.tsx';
import { PopularityBadge } from '../../PopularityBadge/index.tsx';
import { PosterImage } from '../../PosterImage/index.tsx';
import { RecommendationsCarousel } from '../../RecommendationsCarousel/index.tsx';
import { Query as ReviewsBadge } from '../../ReviewsBadge/index.ts';
import { SeasonsCarousel } from '../../SeasonsCarousel/index.tsx';
import { TextLink } from '../../TextLink/index.tsx';
import { Large as VoteAverageBadge } from '../../VoteAverageBadge/index.tsx';
import { JustWatchLogo } from '../../WatchProviders/JustWatchLogo.tsx';
import { WatchProvidersQuery } from '../../WatchProviders/index.ts';
import {
  CastAndCrewHighlightsContainer,
  CertificationIconContainer,
  DetailsBody,
  GenresContainer,
  PosterImageContainer,
  PosterImageOverviewContainer,
  PoweredBy,
  RatingsContainer,
  SectionA,
  WatchProvidersHeadingContainer,
} from '../styled.ts';
import { type Props } from './types.ts';

export const SeasonDetails = ({ data, setActivePreview }: Props) => {
  const { airDate, episodeCount, id, name, overview, posterPath, seasonNumber } = data.season;
  const { contentRatings, genres, id: tvId, name: tvName, popularity, voteAverage, voteCount } = data.tv;
  const theme = useTheme();
  const { config, uaParser } = useToolbelt();
  const [activeVideo, setActiveVideo] = useState<ActiveVideo | undefined>();
  const isAncestorSlideoutDrawer = useIsAncestor(Ancestor.ListingPreview);
  const certification = getTvCertFromContentRatings(contentRatings ?? [], config.country);
  const formattedGenres = formatGenres(genres);

  const memoTvDetails = useMemo(
    () => (
      <>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <Typography sx={{ paddingRight: '52px', position: 'relative' }} variant="h4">
            <ConditionalTextLink link={isAncestorSlideoutDrawer} to={`/details/season/${String(id)}`}>
              <strong>{name}</strong>
            </ConditionalTextLink>
            {isAncestorSlideoutDrawer ? (
              <IconButton
                aria-label="Open preview in full"
                component={Link}
                sx={{
                  color: theme.palette.grey[100],
                  position: 'absolute',
                  right: 0,
                  top: 0,
                }}
                to={`/details/season/${String(id)}`}
              >
                <ArrowForwardIos />
              </IconButton>
            ) : undefined}
          </Typography>
          <Typography variant="h6">
            <TextLink to={`/details/tv/${String(tvId)}`}>{tvName}</TextLink>
          </Typography>
          <div>
            <Typography sx={{ color: theme.supportingTextPrimary }} variant="body2">
              {airDate ? <span>{formatAirDate(airDate, 'yyyy')}</span> : undefined}
              {formattedGenres ? (
                <GenresContainer>
                  <FiberManualRecord
                    sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
                  />
                  <span>{formattedGenres}</span>
                </GenresContainer>
              ) : undefined}
              {episodeCount ? (
                <>
                  <FiberManualRecord
                    sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
                  />
                  <span>{formatNumberOfEpisodes(episodeCount)}</span>
                </>
              ) : undefined}
            </Typography>
          </div>
          <SectionA sx={{ display: 'flex', marginBottom: theme.spacing(1.5), marginTop: theme.spacing(1.5) }}>
            {certification ? (
              <CertificationIconContainer>
                <CertificationIcon
                  certification={certification}
                  height={50}
                  id={tvId}
                  type={ScreenType.Tv}
                  width={50}
                />
              </CertificationIconContainer>
            ) : undefined}
            <RatingsContainer>
              {isAncestorSlideoutDrawer ? undefined : <ReviewsBadge id={tvId} title={tvName} type={ScreenType.Tv} />}
              <VoteAverageBadge
                sx={{
                  [theme.breakpoints.up('md')]: {
                    marginLeft: theme.spacing(2),
                  },
                  marginLeft: theme.spacing(1),
                }}
                voteAverage={voteAverage}
                voteCount={voteCount}
              />
              <PopularityBadge
                popularity={popularity}
                sx={{
                  [theme.breakpoints.up('md')]: {
                    marginLeft: theme.spacing(2),
                  },
                  [theme.breakpoints.down('sm')]: {
                    display: 'none',
                  },
                  marginLeft: theme.spacing(1),
                }}
              />
            </RatingsContainer>
          </SectionA>
        </Section>
        <Section color={SectionColor.Black}>
          <PlayerSelectorTabs id={tvId} seasonNumber={seasonNumber} title={tvName} type={ScreenFormatType.Tv} />
        </Section>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <DetailsBody>
            <PosterImageContainer>
              <PosterImage
                id={id}
                pathIds={[tvId, seasonNumber]}
                src={buildImagePath(posterPath, ImageSize.Poster, PosterSize.MD, config.images)}
                title={name}
                type={SectionType.Season}
              />
            </PosterImageContainer>
            <PosterImageOverviewContainer>
              {overview ? <Overview content={overview} id={id} name={name} /> : undefined}
            </PosterImageOverviewContainer>
            <CastAndCrewHighlightsContainer hasOverview={!!overview}>
              <CastAndCrewHighlights id={id} type={ScreenFormatType.Season} />
            </CastAndCrewHighlightsContainer>
          </DetailsBody>
        </Section>
        {!isAncestorSlideoutDrawer && (
          <>
            <WatchProvidersQuery id={tvId} type={ScreenType.Tv}>
              {watchProviders =>
                watchProviders ? (
                  <Section
                    color={SectionColor.Grey}
                    threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}
                    title={
                      <WatchProvidersHeadingContainer>
                        {get<number>(watchProviders, 'props.providers.length') > 0 ? (
                          <>
                            <SectionHeading variant="h6">Where to watch</SectionHeading>
                            <div>
                              <PoweredBy>Powered by</PoweredBy>
                              <JustWatchLogo />
                            </div>
                          </>
                        ) : (
                          <Skeleton sx={{ fontSize: '1.25rem' }} variant="text" width={120} />
                        )}
                      </WatchProvidersHeadingContainer>
                    }
                  >
                    {watchProviders}
                  </Section>
                ) : undefined
              }
            </WatchProvidersQuery>
            <EpisodesCarousel id={id} seasonNumber={seasonNumber} setActivePreview={setActivePreview} tvId={tvId}>
              {episodesCarousel =>
                episodesCarousel ? <Section title="Episodes">{episodesCarousel}</Section> : undefined
              }
            </EpisodesCarousel>
            <CastAndCrewCarousel
              id={id}
              setActivePreview={setActivePreview}
              subType="cast"
              type={ScreenFormatType.Season}
            >
              {castCarousel => (castCarousel ? <Section title="Cast">{castCarousel}</Section> : undefined)}
            </CastAndCrewCarousel>
            <CastAndCrewCarousel
              id={id}
              setActivePreview={setActivePreview}
              subType="crew"
              type={ScreenFormatType.Season}
            >
              {castCarousel => (castCarousel ? <Section title="Crew">{castCarousel}</Section> : undefined)}
            </CastAndCrewCarousel>
            <SeasonsCarousel id={tvId} setActivePreview={setActivePreview}>
              {seasonsCarousel => (seasonsCarousel ? <Section title="Seasons">{seasonsCarousel}</Section> : undefined)}
            </SeasonsCarousel>
            <RecommendationsCarousel id={tvId} setActivePreview={setActivePreview} type={ScreenType.Tv}>
              {recommendationsCarousel =>
                recommendationsCarousel ? <Section title="Recommended">{recommendationsCarousel}</Section> : undefined
              }
            </RecommendationsCarousel>
          </>
        )}
      </>
    ),
    [id], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      {activeVideo?.src && activeVideo.src.length > 0 ? (
        <PlayerSelectorModal
          activeVideo={activeVideo}
          onClose={() => {
            setActiveVideo(undefined);
            void disableFullscreenLock(uaParser);
          }}
          type={ScreenType.Tv}
        />
      ) : undefined}
      {memoTvDetails}
    </div>
  );
};
